'use client';

import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import {styled} from '@mui/material/styles';
import {useId} from 'react';
import {AppIcon, FaCaretDownIcon} from '../icon';
import {Typography} from '../primitives';

import {FIELD_PADDING_X, fieldShellStyles, menuShadow} from './field-styles';
import {FieldShell} from './FieldShell';

const SelectInput = styled(InputBase, {
  // `notched` is injected by MUI Select for outlined inputs; InputBase would leak it to the DOM.
  shouldForwardProp: p => p !== 'oError' && p !== 'notched',
})<{oError?: boolean}>(({theme, oError}) => ({
  ...fieldShellStyles(theme, {error: oError}),
  '& .MuiInputBase-input': {
    display: 'flex',
    alignItems: 'center',
    minHeight: 'auto',
    padding: 0,
    paddingInlineEnd: 28,
    color: theme.palette.colors.text.neutral.strong,
    '&:focus': {backgroundColor: 'transparent'},
  },
}));

const Item = styled(MenuItem)(({theme}) => ({
  paddingInline: FIELD_PADDING_X,
  paddingBlock: theme.spacing(0.75),
  color: theme.palette.colors.text.neutral.strong,
}));

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: SelectOption[];
  required?: boolean;
  error?: boolean;
  helperText?: string;
  name?: string;
  disabled?: boolean;
}

// MUI positions and rotates the select arrow through the className it passes here,
// so the wrapper must forward it to the glyph.
function SelectChevron({className}: {className?: string}) {
  return <AppIcon icon={FaCaretDownIcon} className={className} aria-hidden />;
}

export function Select({label, value, onChange, onBlur, options, required, error, helperText, name, disabled}: SelectProps) {
  const helperId = useId();
  return (
    <FieldShell label={label} required={required} showLabel={!value} helperId={helperId} error={error} helperText={helperText}>
      <MuiSelect
        fullWidth
        displayEmpty
        value={value}
        name={name}
        disabled={disabled}
        onChange={e => {
          onChange(e.target.value);
        }}
        onBlur={onBlur}
        input={<SelectInput oError={error} />}
        IconComponent={SelectChevron}
        renderValue={selected => {
          const selectedLabel = options.find(o => o.value === selected)?.label;
          return selectedLabel ? (
            <Typography as="span" size="body-2" weight="regular" sx={{color: 'inherit'}}>
              {selectedLabel}
            </Typography>
          ) : null;
        }}
        inputProps={{
          'aria-label': label,
          'aria-required': required || undefined,
          'aria-invalid': error || undefined,
          'aria-describedby': helperText ? helperId : undefined,
        }}
        MenuProps={{
          slotProps: {
            paper: {
              sx: theme => ({
                marginBlockStart: 0.5,
                borderRadius: `${theme.radii.sm}px`,
                boxShadow: menuShadow(theme),
                border: `1px solid ${theme.palette.colors.border.neutral.subtle}`,
              }),
            },
          },
        }}
        sx={theme => ({
          '& .MuiSelect-icon': {
            color: theme.palette.colors.icon.neutral.default,
            right: 'unset',
            insetInlineEnd: 14,
          },
        })}
      >
        {options.map(o => (
          <Item key={o.value} value={o.value}>
            <Typography as="span" size="body-2" weight="regular" sx={{color: 'inherit'}}>
              {o.label}
            </Typography>
          </Item>
        ))}
      </MuiSelect>
    </FieldShell>
  );
}

Select.displayName = 'Select';
